// The scope lives in navigation.ts, where all of this console's cross-boundary scopes are
// collected so "what can this console reach" is answerable from one file. Re-exported so a reader
// of this module finds it, and so channel-scopes.mjs sees one literal rather than two.
export { TENANTS_SCOPE } from "./navigation.ts";

import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow, type DetailField } from "./board.ts";

/**
 * What the tenant board does to the tenants TEN-01 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `tenants`:
 *
 * ```
 * tenants:['Tenant list and provisioning','TEN-06',
 *   'Every business on the platform. Provisioning is a saga; a failed step compensates instead of
 *    leaving a half-made tenant.',
 *   [['Tenants','5','done','4 active'],['Provisioning','1','active','running'],
 *    ['Suspended','1','closed','billing'],['Failed sagas','0','done','none']],
 *   ['Tenant','ID','Region'],
 *   [['East Africa Mobility Ltd','ten_7Q2M','KE · UG · TZ','done','active'],
 *    ['Harbour Pharmacy Group','ten_5HP2','TZ','active','provisioning · step 4 of 6'],
 *    ['Sahel Grocers SA','ten_B04N','SN · ML','closed','suspended']],
 *   'info','Opening a tenant switches context with a confirmation naming both sides.']
 * ```
 *
 * ## This page could not list the thing it is a board of
 *
 * Its recorded first absence was "POST /v1/tenants exists; there is no GET, so the board that
 * discovers tenants cannot list them" — so a platform administrator could create a tenant and never
 * see it again, and **every other read on this console needs an identifier this page was supposed to
 * supply**. That listing is published now, with the scope and the cursor argued in
 * `services/tenant/tenant-registry`, and it is the reason this file exists.
 *
 * ## What is drawn from data and what is not
 *
 * | drawn                                   | source                                                       |
 * | --------------------------------------- | ------------------------------------------------------------ |
 * | the rows                                | TEN-01 `GET /v1/tenants`                                     |
 * | `Tenant` — `East Africa Mobility Ltd`   | `displayName`, which is a real name and not an identifier    |
 * | `ID` — `ten_7Q2M`                       | `id`, a UUID. The design's short code does not exist (R-11)  |
 * | `Region` — `KE · UG · TZ`               | **nothing** — swept across every contract, drawn as absent   |
 * | `done · active`, `closed · suspended`   | `status`, which is the contract's own four-value enum        |
 * | `active · provisioning · step 4 of 6`   | the first half is `status`; **the step has no source**        |
 * | the four tiles                          | no listing publishes a total (R-04); no tile row here        |
 * | the primary command                     | drawn and refused — `PROVISION_REFUSAL`                      |
 *
 * ## The three absences that keep this page degraded
 *
 * **A tenant has no region.** The design's third column is `KE · UG · TZ`. `tenant.regions`,
 * `tenant.countries` and `tenant.regionCodes` were swept across all 147 contracts and none exists.
 * The cell is the design's own `—` with the reason on it, never a country guessed from a slug.
 *
 * **A provisioning tenant has no step.** The design writes `provisioning · step 4 of 6`, which is
 * the saga's progress. TEN-01 publishes `status: provisioning` and nothing about how far it has
 * got; TEN-06's `GET /v1/provisioning/{id}` holds that and takes a provisioning identifier, which
 * is not a tenant identifier and which nothing on this page can discover. So the badge is the
 * status and the step is omitted rather than invented — a step number on a saga nobody is reading
 * is the clearest case of a figure an operator would act on.
 *
 * **The short tenant code does not exist.** The design's `ten_7Q2M` is a human-sized identifier and
 * TEN-01 publishes a UUID. The column draws the UUID in mono, which is what it is. R-11.
 */

/** TEN-01's published status enum, as one name. */
export type TenantStatus = "provisioning" | "active" | "suspended" | "terminated";

/** One tenant, as TEN-01's listing publishes it. */
export interface Tenant {
  readonly id: string;
  readonly slug: string;
  readonly legalName: string;
  readonly displayName: string;
  readonly status: TenantStatus;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * The state class and the word, from `status`.
 *
 * | status         | class     | word           | where the word comes from                     |
 * | -------------- | --------- | -------------- | --------------------------------------------- |
 * | `active`       | `done`    | `active`       | the design's `done · active` row              |
 * | `provisioning` | `active`  | `provisioning` | the design's `active · provisioning · step …` |
 * | `suspended`    | `closed`  | `suspended`    | the design's `closed · suspended` row         |
 * | `terminated`   | `closed`  | `terminated`   | the contract's own word; the design draws none |
 *
 * **`provisioning` is the design's `active` class and `active` is its `done` class**, which reads
 * backwards and is right: the design's `active` means "something is happening now", and a tenant
 * being provisioned is the only row on this board where something is. An active tenant is a settled
 * state, which is what `done` is for.
 *
 * **`terminated` is `closed` and not `failed`.** A terminated tenant is one somebody ended on
 * purpose; the design's `failed` class is red and means something went wrong. There is no status on
 * this enum for a failed provisioning — which is also why the design's `Failed sagas · 0` tile has
 * no source here.
 */
export function stateOf(tenant: Tenant): { classKey: StateClassKey; label: string } {
  switch (tenant.status) {
    case "active":
      return { classKey: "done", label: "active" };
    case "provisioning":
      return { classKey: "active", label: "provisioning" };
    case "suspended":
      return { classKey: "closed", label: "suspended" };
    case "terminated":
      return { classKey: "closed", label: "terminated" };
  }
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(tenants: readonly Tenant[]): readonly string[] {
  return [...new Set(tenants.map((tenant) => stateOf(tenant).label))];
}

/**
 * One row, with its detail panel.
 *
 * The panel draws `slug` and `legalName`, which the grid has no column for and which are the two
 * things a platform administrator needs beyond the name: the slug is what every URL and every
 * tenant-scoped path is keyed on, and the legal name is what a contract or an invoice says. Both
 * are required fields on every row, so neither is ever absent.
 */
export function tenantRow(tenant: Tenant): BoardRow {
  const state = stateOf(tenant);

  return {
    id: tenant.id,
    cells: [
      {
        text: tenant.displayName,
        title: `The tenant's display name. Its legal name is ${tenant.legalName}.`,
      },
      {
        text: tenant.id,
        // Mono, which the design already does for a value matching its own `ten_` prefix — and this
        // is that value, at the length the registry actually mints.
        mono: true,
        title:
          "The tenant identifier, as TEN-01 mints it. The design writes a short code — ten_7Q2M — and nothing on any contract publishes one. R-11.",
      },
      {
        text: "",
        absent: true,
        title:
          "No tenant region is published anywhere. tenant.regions, tenant.countries and tenant.regionCodes were swept across every contract and none exists. Not derived from the slug, which would be a country guessed from a word.",
      },
    ],
    state: {
      ...state,
      // The contract's own word, on the badge's title. `BoardState` carries no service code — this
      // console's badge takes the class and the word and leaves the provenance to the title, which
      // is where a reader asking "what did the service actually say" looks.
      title: `TEN-01 publishes status ${tenant.status}. ${describeStatus(tenant.status)}`,
    },
    // The design tints its `human` and `failed` rows and this board can reach neither: no status on
    // TEN-01's enum maps to either class. Left untinted rather than tinting `suspended`, which the
    // design draws plain.
    detail: detailFor(tenant),
    history: historyFor(tenant),
    // The design's own form glyph for this page, which the detail panel's head takes.
    glyph: "domain",
  };
}

/**
 * What a status means, for the badge's title.
 *
 * One sentence per published value. The pair worth separating is `suspended` and `terminated`: the
 * design draws `suspended · billing` and nothing publishes a reason, so a reader must not take the
 * word as naming one.
 */
function describeStatus(status: TenantStatus): string {
  switch (status) {
    case "provisioning":
      return "The provisioning saga is running. TEN-01 publishes no step, and TEN-06's progress read takes a provisioning identifier this page cannot discover.";
    case "active":
      return "The tenant is live. Every tenant-scoped operation on the platform checks this status before acting.";
    case "suspended":
      return "Somebody suspended it. TEN-01 publishes no reason, so the design's own `billing` is not a word this board can say.";
    case "terminated":
      return "It was ended on purpose. Not a failure: no status on this enum is one, which is why the design's Failed sagas tile has no source here.";
  }
}

function detailFor(tenant: Tenant): readonly DetailField[] {
  return [
    { label: "Slug", value: tenant.slug, mono: true },
    { label: "Legal name", value: tenant.legalName },
    { label: "Status", value: tenant.status, mono: true },
    {
      label: "Region",
      value: "—",
      absent: true,
    },
    {
      label: "Provisioning step",
      value: "—",
      absent: true,
    },
  ];
}

function historyFor(tenant: Tenant): readonly DetailField[] {
  return [
    { label: "Registered", value: asDay(tenant.createdAt) },
    { label: "Last changed", value: asDay(tenant.updatedAt) },
    { label: "Version", value: `v${String(tenant.version)}`, mono: true },
  ];
}

/**
 * Why the designed provisioning form cannot be sent, in the words of what is missing.
 *
 * The design's `FORMS.tenants` collects six fields and `POST /v1/provisioning` publishes **no
 * request body at all** — not a thin one, none. So there is nothing to fill in and nothing to
 * invent that `CLAUDE.md` §2 would permit.
 *
 * `POST /v1/tenants` exists and is deliberately not offered in its place. It takes a slug, a legal
 * name and a display name, and takes none of the six things the form asks for — so a button
 * labelled `Provision tenant` that created a bare tenant record would do a different thing from
 * the one it names, and leave a tenant with no provisioning behind it. That is worse than a
 * refusal, and it is the kind of substitution that only looks helpful until somebody uses it.
 */
export const PROVISION_REFUSAL =
  "Not available. POST /v1/provisioning publishes no request body at all, so the designed six-field form has nowhere to send anything. POST /v1/tenants exists and takes none of those six fields, so it is not a substitute: it would create a bare tenant with no provisioning behind it.";

/**
 * What this board owes a platform administrator beyond its rows.
 *
 * Two sentences, and the second is this board's own. The design's badge for a provisioning tenant
 * reads `provisioning · step 4 of 6`, and an administrator watching a tenant come up is watching
 * for exactly that number — so a board that silently drew the status alone would look like a board
 * whose tenant had stopped moving.
 */
export function listingNote(tenants: readonly Tenant[] | undefined): string | undefined {
  if (tenants === undefined) return undefined;
  const provisioning = tenants.filter((tenant) => tenant.status === "provisioning").length;
  const regions = "No tenant region is published anywhere, so that column is drawn as an absence.";
  if (provisioning === 0) return regions;
  return (
    `${regions} ${String(provisioning)} tenant(s) are provisioning, and TEN-01 publishes no step: ` +
    `the saga's progress is TEN-06's, behind a provisioning identifier that nothing on this page ` +
    `can discover. So the badge says provisioning and stops there.`
  );
}
